import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { SelectCountryField } from './SelectCountryField';
import { SelectLanguageField } from './SelectLanguageField';
import { SelectCurrenciesField } from './SelectCurrenciesField';
import { SelectTimezoneField } from './SelectTimezoneField';
import { onCompanySettingListsLoad, onCompanySettingsSave } from '../../../store/Settings/actions';
import Loading from '../../../components/Loading';
import '../setting.css';
import { FormField } from '../../../components/FormField';
import * as yup from 'yup';

const urlRegex = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/;

export const Settings = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(onCompanySettingListsLoad());
    }, []);

    const { countries_list, currencies_list, timezones_list, languages_list, country, currency, timezone, language, privacy_policy, terms_conditions, loading } = useSelector(({ settings }) => ({
        loading: settings.loading,
        countries_list: settings.company_setting_lists.countries_list,
        currencies_list: settings.company_setting_lists.currencies_list,
        timezones_list: settings.company_setting_lists.timezones_list,
        languages_list: settings.company_setting_lists.languages_list,
        country: settings.company_preferences.country,
        currency: settings.company_preferences.currency,
        timezone: settings.company_preferences.timezone,
        language: settings.company_preferences.language,
        privacy_policy: settings.company_preferences.privacy_policy,
        terms_conditions: settings.company_preferences.terms_conditions,
    }));

    const validationSchema = yup.object().shape({
        country: yup.mixed().required(t('Required!')),
        currency: yup.mixed().required(t('Required!')),
        timezone: yup.mixed().required(t('Required!')),
        language: yup.mixed().required(t('Required!')),
        privacy_policy: yup.string().matches(urlRegex, t('URL is not valid')).required(t('Required!')),
        terms_conditions: yup.string().matches(urlRegex, t('URL is not valid')).required(t('Required!')),
    });

    return (
        <Loading loading={loading}>
            <Formik
                enableReinitialize
                initialValues={{
                    country: country ? { value: country.id, label: country.name } : {},
                    currency: currency ? { value: currency.id, label: currency.name } : {},
                    timezone: timezone ? { value: timezone.id, label: timezone.name } : {},
                    language: language ? { value: language.id, label: language.name } : {},
                    privacy_policy: privacy_policy || '',
                    terms_conditions: terms_conditions || '',
                }}
                validationSchema={validationSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={async (values) => {
                    dispatch(onCompanySettingsSave(values));
                }}
            >
                {({ errors, values }) => (
                    <Form>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg shadow-md bg-white pt-5 pr-16 pb-4 pl-5">
                                <p className="font-bold text-base">{t('General Settings')}</p>
                                <span className="text-xs font-medium text-[#676767]">{t('Update your counrty and language preferences')}</span>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Language')} htmlFor="language" required error={errors.language}>
                                        <Field name="language" component={SelectLanguageField} options={languages_list} language={values.language} />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Country')} htmlFor="country" required error={errors.country}>
                                        <Field name="country" component={SelectCountryField} options={countries_list} country={values.country} />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Currency')} htmlFor="currency" required error={errors.currency}>
                                        <Field name="currency" component={SelectCurrenciesField} options={currencies_list} currency={values.currency} />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Time Zone')} htmlFor="timezone" required error={errors.timezone}>
                                        <Field name="timezone" component={SelectTimezoneField} options={timezones_list} timezone={values.timezone} />
                                    </FormField>
                                </div>
                            </div>

                            <div className="rounded-lg shadow-md bg-white pt-5 pr-16 pb-8 pl-5">
                                <p className="font-bold text-base">{t('GDPR Policies')}</p>
                                <span className="text-xs font-medium text-[#676767]">{t('Provide URL to your privacy policy and terms & conditions')}</span>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Privacy Policy')} htmlFor="privacy_policy" required error={errors.privacy_policy}>
                                        <Field type="text" name="privacy_policy" placeholder={t('Privacy Policy')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Terms & Conditions')} htmlFor="terms_conditions" required error={errors.terms_conditions}>
                                        <Field type="text" name="terms_conditions" placeholder={t('Terms & Conditions')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>
                            </div>
                        </div>

                        <div className="py-3 text-right">
                            <button type="submit" className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-sm font-bold rounded-lg text-white bg-gray-900 hover:bg-gray-700 activ:bg-gray-800 focus:outline-none">
                                {t('Save')}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Loading>
    );
};
